import { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../i18n';
import { parseMarkdownToSections } from '../utils/markdownParser';
import type { DocumentSection } from '../utils/markdownParser';
import DocumentOutline from './DocumentOutline';
import SectionCard from './SectionCard';
import StyledMarkdown from './StyledMarkdown';
import MarkdownErrorBoundary from './MarkdownErrorBoundary';
import { FileOutput, Layers, Loader2 } from 'lucide-react';
import type { SectionKey } from '../prompts';

interface DocumentViewerProps {
  content: string;
  projectName: string;
  projectId: string;
  sectionKey: SectionKey;
  locale?: string;
  loading?: boolean;
  onExportSection?: (section: DocumentSection) => void;
}

export default function DocumentViewer({
  content,
  projectName,
  projectId,
  sectionKey,
  locale,
  loading,
  onExportSection,
}: DocumentViewerProps) {
  const { t } = useLanguage();
  const [activeId, setActiveId] = useState<string | undefined>();
  const mainRef = useRef<HTMLDivElement>(null);

  const parsed = useMemo(() => parseMarkdownToSections(content), [content]);

  useEffect(() => {
    if (parsed.sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          setActiveId(visible.target.id);
        }
      },
      {
        root: mainRef.current,
        rootMargin: '-15% 0px -70% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    parsed.flatSections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [parsed]);

  const handleExport = (section: DocumentSection) => {
    if (onExportSection) {
      onExportSection(section);
    }
  };

  const exportAllSections = () => {
    parsed.sections.forEach((section) => handleExport(section));
  };

  if (!content.trim()) {
    return (
      <div className="text-center py-12 text-slate">
        {loading ? (
          <>
            <Loader2 size={48} className="mx-auto mb-4 text-red-400 animate-spin" />
            <p>{t.project.generating}</p>
          </>
        ) : (
          <>
            <Layers size={48} className="mx-auto mb-4 text-cloud" />
            <p>No content to display.</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex gap-6 document-page">
      <DocumentOutline
        sections={parsed.sections}
        activeId={activeId}
        onNavigate={(id) => {
          const el = document.getElementById(id);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveId(id);
          }
        }}
      />

      <div ref={mainRef} className="flex-1 min-w-0 overflow-y-auto">
        {parsed.title && (
          <div className="mb-8">
            <h1 className="text-3xl font-extrabold text-ink mb-2">{parsed.title}</h1>
            <p className="text-slate">{projectName}</p>
          </div>
        )}

        {parsed.sections.length === 0 ? (
          <div className="bg-white border border-cloud p-6 shadow-sm">
            <MarkdownErrorBoundary content={content}>
              <StyledMarkdown content={content} />
            </MarkdownErrorBoundary>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4 no-print">
              <p className="text-sm text-slate">
                {t.document.sections(parsed.sections.length)} · {t.document.blocks(parsed.flatSections.length)}
              </p>
              <button
                onClick={exportAllSections}
                className="text-sm px-3 py-1.5 text-ink hover:bg-mist flex items-center gap-2 border border-cloud transition-colors"
              >
                <FileOutput size={14} />
                {t.document.exportAll}
              </button>
            </div>

            {parsed.sections.map((section) => (
              <SectionCard
                key={section.id}
                section={section}
                projectName={projectName}
                sectionKey={sectionKey}
                projectId={projectId}
                locale={locale}
                sectionContent={content}
                onExport={handleExport}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
